import "./EditorModelViewer.scss";
import "@google/model-viewer";

import { useEffect, useCallback, useState, useRef } from "react";
import { useCurrentVariant } from "../../contexts/useCurrentVariant";
import { useCurrentProduct } from "../../contexts/useCurrentProduct";
import { useConfig } from "../../contexts/useConfig";
import { getVariantUrl } from "../../hooks/getVariantUrl.js";
import { getPropertyOrDefault } from "../../helpers/helpers";
import { useHotspotEditor } from "../../contexts/useHotspotEditor";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faLockOpen, faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";
import ContentIcon from "../ContentIcon/ContentIcon";

// Helper functions for coordinate formatting (outside component to avoid recreation)
const parseCoordinate = (coord) => {
   if (typeof coord === 'string') {
      return coord.replace('m', '');
   }
   return coord;
};

const getPositionString = (pos) => {
   if (!pos) return "0m 0m 0m";
   return `${parseCoordinate(pos.x)}m ${parseCoordinate(pos.y)}m ${parseCoordinate(pos.z)}m`;
};

const getNormalString = (normal) => {
   if (!normal) return "0m 1m 0m";
   return `${parseCoordinate(normal.x)}m ${parseCoordinate(normal.y)}m ${parseCoordinate(normal.z)}m`;
};

function EditorModelViewer() {
   const [currentVariant] = useCurrentVariant();
   const [currentProduct] = useCurrentProduct();
   const [config] = useConfig();
   const modelViewerRef = useRef(null);
   
   const {
      editorMode,
      setEditorMode,
      selectedHotspotIndex,
      setSelectedHotspotIndex,
      hotspots,
      addHotspot,
      updateHotspotPosition,
      updateHotspotNormal
   } = useHotspotEditor();

   const [modelLoaded, setModelLoaded] = useState(false);
   const [cameraLocked, setCameraLocked] = useState(false);
   const [isFullscreen, setIsFullscreen] = useState(false);

   const androidAndWebModelFormat = currentVariant.glb ? currentVariant.glb : currentVariant.gltf;
   const androidAndWebModelSrc = getVariantUrl(currentProduct.uuid, androidAndWebModelFormat);
   const toneMapping = getPropertyOrDefault(config["toneMapping"], "auto");

   // Toggle camera lock
   const toggleCameraLock = useCallback(() => {
      const mv = modelViewerRef.current;
      if (!mv) return;

      if (cameraLocked) {
         mv.setAttribute('camera-controls', '');
         setCameraLocked(false);
      } else {
         mv.removeAttribute('camera-controls');
         setCameraLocked(true);
      }
   }, [cameraLocked]);

   // Toggle fullscreen
   const toggleFullscreen = useCallback(() => {
      const container = document.querySelector('.editor-model-viewer-container');
      if (!container) return;

      if (!isFullscreen) {
         container.classList.add('fullscreen');
         setIsFullscreen(true);
      } else {
         container.classList.remove('fullscreen');
         setIsFullscreen(false);
      }
   }, [isFullscreen]);

   // Handle click on model for adding/editing hotspots
   const handleModelClick = useCallback((event) => {
      if (!modelViewerRef.current || !modelLoaded) return;

      const mv = modelViewerRef.current;
      
      // Get the model-viewer's shadow root canvas for accurate positioning
      const shadowRoot = mv.shadowRoot;
      const canvas = shadowRoot?.querySelector('canvas');
      
      let x, y;
      
      if (canvas) {
         // Use canvas rect for more accurate positioning
         const canvasRect = canvas.getBoundingClientRect();
         x = event.clientX - canvasRect.left;
         y = event.clientY - canvasRect.top;
      } else {
         // Fallback to model-viewer rect
         const rect = mv.getBoundingClientRect();
         x = event.clientX - rect.left;
         y = event.clientY - rect.top;
      }

      // Get position and normal from the click point
      const hit = mv.positionAndNormalFromPoint(x, y);
      
      if (!hit) {
         console.log("No surface hit at this point - click was outside the model");
         return;
      }

      const { position, normal } = hit;

      switch (editorMode) {
         case 'add':
            addHotspot(position, normal);
            break;
         case 'edit-position':
            if (selectedHotspotIndex !== null) {
               updateHotspotPosition(selectedHotspotIndex, position);
            }
            break;
         case 'edit-normal':
            if (selectedHotspotIndex !== null) {
               updateHotspotNormal(selectedHotspotIndex, normal);
            }
            break;
         default:
            // In select mode, clicking the model background does nothing
            break;
      }
   }, [editorMode, selectedHotspotIndex, addHotspot, updateHotspotPosition, updateHotspotNormal, modelLoaded]);

   // Handle hotspot click
   const handleHotspotClick = useCallback((index, event) => {
      event.stopPropagation();
      
      if (editorMode === 'select') {
         setSelectedHotspotIndex(index);
      }
   }, [editorMode, setSelectedHotspotIndex]);

   // Setup click handler
   useEffect(() => {
      const mv = modelViewerRef.current;
      if (!mv) return;

      const handleClick = (e) => {
         // Only process if we didn't click on a hotspot button
         if (e.target.closest('.editor-hotspot')) return;
         handleModelClick(e);
      };

      mv.addEventListener('click', handleClick);
      return () => mv.removeEventListener('click', handleClick);
   }, [handleModelClick]);

   // Handle model loaded event
   useEffect(() => {
      const mv = modelViewerRef.current;
      if (!mv) return;

      const handleLoad = () => {
         setModelLoaded(true);
         console.log("Editor model loaded");
      };

      mv.addEventListener('load', handleLoad);
      return () => mv.removeEventListener('load', handleLoad);
   }, []);

   // Force hotspot update when positions change by updating model-viewer's hotspot
   useEffect(() => {
      const mv = modelViewerRef.current;
      if (!mv || !modelLoaded) return;

      // Update each hotspot's position and normal directly on the DOM element
      hotspots.forEach((hotspot) => {
         const slotName = `hotspot-${hotspot.id}`;
         const element = mv.querySelector(`[slot="${slotName}"]`);
         if (element) {
            element.dataset.position = getPositionString(hotspot.position);
            element.dataset.normal = getNormalString(hotspot.normal);
            // Trigger model-viewer to update the hotspot position
            mv.updateHotspot({
               name: slotName,
               position: getPositionString(hotspot.position),
               normal: getNormalString(hotspot.normal)
            });
         }
      });
   }, [hotspots, modelLoaded]);

   // Generate a unique key based on hotspot data to force re-render
   const getHotspotKey = (hotspot) => {
      const posStr = hotspot.position ? 
         `${hotspot.position.x}-${hotspot.position.y}-${hotspot.position.z}` : '';
      const normStr = hotspot.normal ? 
         `${hotspot.normal.x}-${hotspot.normal.y}-${hotspot.normal.z}` : '';
      return `${hotspot.id}-${posStr}-${normStr}`;
   };

   // Get cursor style based on mode
   const getCursorStyle = () => {
      switch (editorMode) {
         case 'add':
            return 'crosshair';
         case 'edit-position':
         case 'edit-normal':
            return 'move';
         default:
            return 'default';
      }
   };

   return (
      <div className="editor-model-viewer-container">
         {/* Toolbar */}
         <div className="editor-toolbar">
            <button
               className={`toolbar-btn ${cameraLocked ? 'active' : ''}`}
               onClick={toggleCameraLock}
               title={cameraLocked ? 'Unlock Camera (enables rotation)' : 'Lock Camera (prevents rotation)'}
            >
               <FontAwesomeIcon icon={cameraLocked ? faLock : faLockOpen} />
               <span>{cameraLocked ? 'Unlock Camera' : 'Lock Camera'}</span>
            </button>
            <button
               className="toolbar-btn"
               onClick={toggleFullscreen}
               title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
               <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
               <span>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
            </button>
         </div>

         <div 
            className={`editor-model-viewer ${editorMode !== 'select' ? 'editing' : ''} ${cameraLocked ? 'camera-locked' : ''}`}
            style={{ cursor: getCursorStyle() }}
         >
            <model-viewer
               ref={modelViewerRef}
               className="model-viewer"
               alt="Hotspot Editor Model Viewer"
               src={androidAndWebModelSrc}
               camera-controls
               tone-mapping={toneMapping}
               environment-image={config["background-environment"]}
               skybox-image={config["skybox-environment"]}
               exposure={config["exposure"] ?? 1}
               shadow-intensity={config["shadow-intensity"] ?? "1"}
               shadow-softness={config["shadow-softness"] ?? "1"}
               interaction-prompt="none"
            >
               {/* Render editable hotspots */}
               {hotspots.map((hotspot, index) => (
                  <div
                     key={getHotspotKey(hotspot)}
                     className={`editor-hotspot ${selectedHotspotIndex === index ? 'selected' : ''}`}
                     slot={`hotspot-${hotspot.id}`}
                     data-position={getPositionString(hotspot.position)}
                     data-normal={getNormalString(hotspot.normal)}
                     data-visibility-attribute="visible"
                     onClick={(e) => handleHotspotClick(index, e)}
                  >
                     <motion.button
                        className="hotspot-marker"
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                     >
                        <ContentIcon
                           fontAwesomeIconName={hotspot.fontAwesomeIconName}
                           fontAwesomeIconStyle={hotspot.fontAwesomeIconStyle}
                           contentType={hotspot.iconOverride ?? hotspot.contentType}
                        />
                     </motion.button>
                     <div className="hotspot-info">
                        <span className="hotspot-index">#{hotspot.id}</span>
                        <span className="hotspot-title">{hotspot.title || 'Untitled'}</span>
                     </div>
                  </div>
               ))}
            </model-viewer>
         </div>

         {/* Camera Lock Indicator */}
         {cameraLocked && (
            <div className="lock-indicator">
               <FontAwesomeIcon icon={faLock} />
               <span>Camera Locked</span>
            </div>
         )}

         {/* Mode Indicator Overlay */}
         {editorMode !== 'select' && (
            <div className="mode-overlay">
               <div className="mode-badge">
                  {editorMode === 'add' && 'Click on model to add hotspot'}
                  {editorMode === 'edit-position' && 'Click to move hotspot here'}
                  {editorMode === 'edit-normal' && 'Click surface to set facing direction'}
               </div>
               <button className="cancel-mode-btn" onClick={() => setEditorMode('select')}>
                  Cancel (Esc)
               </button>
            </div>
         )}

         {/* Help Text */}
         <div className="editor-help">
            <span>
               {cameraLocked 
                  ? 'Camera locked • Click to place hotspots' 
                  : 'Drag to rotate • Scroll to zoom • Shift+drag to pan'}
            </span>
         </div>
      </div>
   );
}

export default EditorModelViewer;
